
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { authRateLimiter, getSecureErrorMessage, validatePasswordStrength } from '@/utils/securityUtils';
import { logger } from '@/utils/logger';
import { toast } from '@/hooks/use-toast';
import SecurityAuditService from '@/services/securityAuditService';
import { clearAllAuthStorage } from '@/utils/authStorage';

/**
 * Wrap a promise with a timeout to prevent indefinite hanging
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 30000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out. Please try again.')), timeoutMs)
    )
  ]);
};

/**
 * Secure authentication hook with enhanced security measures
 */
export const useSecureAuth = () => {
  const [isLoading, setIsLoading] = useState(false);

  const secureSignIn = async (email: string, password: string, options?: { silent?: boolean }) => {
    setIsLoading(true);
    
    try {
      // Check rate limiting
      if (authRateLimiter.isRateLimited(email)) {
        throw new Error('Too many attempts. Please wait before trying again.');
      }

      // Record the attempt
      authRateLimiter.recordAttempt(email);

      // Validate inputs (but never log passwords)
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      logger.debug('Sign in attempt', { email: email.substring(0, 3) + '***' });

      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password: password // Never log this
        })
      );

      if (error) {
        throw error;
      }

      // Reset rate limiting on successful login
      authRateLimiter.reset(email);
      
      // Log successful login
      await SecurityAuditService.logLoginSuccess();
      
      logger.debug('Sign in successful');
      return { data, error: null };

    } catch (error: any) {
      // Log failed login attempt
      await SecurityAuditService.logLoginFailure(error.message);
      
      const secureMessage = getSecureErrorMessage(error);
      if (!options?.silent) {
        toast({
          title: "Sign In Failed",
          description: secureMessage,
          variant: "destructive",
        });
      }
      
      return { data: null, error: { message: secureMessage } };
    } finally {
      setIsLoading(false);
    }
  };

  const secureSignUp = async (email: string, password: string, metadata?: any, emailRedirectTo?: string) => {
    setIsLoading(true);
    
    try {
      // Check rate limiting
      if (authRateLimiter.isRateLimited(email)) {
        throw new Error('Too many attempts. Please wait before trying again.');
      }

      // Validate password strength - return clear error instead of generic message
      const passwordValidation = validatePasswordStrength(password);
      if (!passwordValidation.isValid) {
        toast({
          title: "Weak Password",
          description: passwordValidation.errors.join(', '),
          variant: "destructive",
        });
        return { data: null, error: { message: passwordValidation.errors.join(', ') } };
      }

      // Record the attempt
      authRateLimiter.recordAttempt(email);

      logger.debug('Sign up attempt', { email: email.substring(0, 3) + '***' });

      const { data, error } = await withTimeout(
        supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password: password, // Never log this
          options: {
            data: metadata || {},
            emailRedirectTo: emailRedirectTo
          }
        })
      );

      if (error) {
        throw error;
      }

      // Reset rate limiting on successful signup
      authRateLimiter.reset(email);
      
      // Log successful signup
      await SecurityAuditService.logSignup(metadata?.user_type);
      
      logger.debug('Sign up successful');
      return { data, error: null };

    } catch (error: any) {
      // Handle "already registered" errors specifically - don't mask these
      if (error?.message?.includes('already registered') || 
          error?.message?.includes('User already registered') ||
          error?.code === 'user_already_exists') {
        toast({
          title: "Account Exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive",
        });
        return { data: null, error: { message: "Account already exists" } };
      }
      
      const secureMessage = getSecureErrorMessage(error);
      toast({
        title: "Sign Up Failed",
        description: secureMessage,
        variant: "destructive",
      });
      
      return { data: null, error: { message: secureMessage } };
    } finally {
      setIsLoading(false);
    }
  };

  const securePasswordReset = async (email: string) => {
    setIsLoading(true);
    
    try {
      // Check rate limiting
      if (authRateLimiter.isRateLimited(email)) {
        throw new Error('Too many attempts. Please wait before trying again.');
      }

      // Record the attempt
      authRateLimiter.recordAttempt(email);

      logger.debug('Password reset attempt', { email: email.substring(0, 3) + '***' });

      const { error } = await withTimeout(
        supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
          redirectTo: `https://godyspo.com/auth/reset-password`
        })
      );

      if (error) {
        throw error;
      }

      // Always show success message (don't reveal if email exists)
      toast({
        title: "Password Reset Sent",
        description: "If an account with that email exists, you'll receive a password reset link.",
      });

      return { error: null };

    } catch (error: any) {
      const secureMessage = getSecureErrorMessage(error);
      toast({
        title: "Password Reset Failed",
        description: secureMessage,
        variant: "destructive",
      });
      
      return { error: { message: secureMessage } };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    secureSignIn,
    secureSignUp,
    securePasswordReset,
    isLoading
  };
};
